/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMemo } from 'react';
import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import Typography from '../../../components/Typography';
import { SeedMix } from '../types/SeedOrderFormType';
import { selectStoreById } from '../../stores/storage/stores.slice';
import MaterialSymbol from '../../../components/MaterialSymbol';
import { colors } from '@farmlands/mui-theme';
import { selectSeedStoreById } from '../storage/seedStores.slice';
import { selectRemoteConfig } from '../../../services/firebase/remote-config/remoteConfig.slice';
import TestIds from '../../../testIds';

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '8px',
  margin: 0,
  [theme.breakpoints.down('sm')]: {
    gap: '4px',
    flexDirection: 'column',
  },
}));

const SeedOrderDeliveryDetails = ({ seed }: { seed: SeedMix }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const seedStore = useSelector(selectSeedStoreById(seed.storeId ?? undefined));
  const store = useSelector(selectStoreById(seed.storeId ?? undefined));
  const remoteConfig = useSelector(selectRemoteConfig);

  const preferredDate =
    seed.paddocks.length === 1
      ? dayjs(seed.paddocks[0].deliveryDate).format('D MMM YYYY').toString()
      : 'Multiples delivery dates';

  const details = useMemo(() => {
    if (remoteConfig.seedStorePickupEnabled && seed.storeId && seedStore) {
      return {
        deliveryType: 'Seed store pickup',
        address: `${seedStore.name}, ${seedStore.address}`,
        icon: 'location_on',
      };
    }

    if (seed.storeId && store) {
      return {
        deliveryType: 'Branch pickup',
        address: store.orgUnitName,
        icon: 'location_on',
      };
    }

    if (seed.deliveryAddress) {
      const { streetAddress, city, postcode } = seed.deliveryAddress;
      return {
        deliveryType: 'Delivery',
        address: `${streetAddress}, ${city}, ${postcode}`,
        icon: 'local_shipping',
      };
    }

    return {
      deliveryType: 'Pickup / Delivery',
      address: 'Select pickup from branch, store or delivery method',
      icon: 'location_on',
    };
  }, [seed]);

  return (
    <Container data-testid={TestIds.feat.seed.deliveryDetails.component}>
      <Box
        display='flex'
        alignItems='center'
        data-testid={TestIds.feat.seed.deliveryDetails.details}
      >
        {details.icon && (
          <MaterialSymbol sx={{ marginRight: '8px', color: colors.primary500 }}>
            {details.icon}
          </MaterialSymbol>
        )}
        <Typography variant='body2' fontWeight='bold' color='primary500'>
          {details.deliveryType}
        </Typography>
      </Box>
      {details.address && (
        <Typography variant='body2' color='primary500'>
          {details.address}
        </Typography>
      )}
      {!isSm && (
        <Typography variant='body2' color='primary500'>
          |
        </Typography>
      )}
      <Box display='flex' flexWrap='wrap'>
        <Typography variant='body2' fontWeight='bold' color='primary500' mr={1}>
          Preferable date:
        </Typography>
        <Typography variant='body2' color='primary500' sx={{ wordBreak: 'break-word' }}>
          {preferredDate}
        </Typography>
      </Box>
    </Container>
  );
};

export default SeedOrderDeliveryDetails;
